import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import TournamentHeader from '../../components/liga/TournamentHeader';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
  INSCRICOES_POR_TORNEIO,
  InscricoesPorTorneioData,
  InscricoesPorTorneioVars,
} from '../../services/torneioService';
import { buscarCategoriaTorneioPorId, CategoriaTorneio, alterarStatusCategoriaTorneio } from '../../services/categoriaTorneioService';
import CategoriaStatusChip from '../../components/CategoriaStatusChip';
import { buscarFilasEsperaPorCategoria, promoverFilaEspera, FilaEspera } from '../../services/filaEsperaService';
import { dispatchToast } from '../../context/ToastContext';
import { useProfile } from '../../context/ProfileContext';
import { useAuth } from '../../context/AuthContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import CategoryAboutTab from './categoryManagement/CategoryAboutTab';
import CategoryRegistrationsTab from './categoryManagement/CategoryRegistrationsTab';
import CategoryMatchesTab from './categoryManagement/CategoryMatchesTab';
import CategoryConfigTab from './categoryManagement/CategoryConfigTab';
import { PageBreadcrumb } from '../../components/navigation';
import { StatusCategoriaTorneio } from '../../types/categoriaTorneio';

const LeagueCategoryManagement: React.FC = () => {
  const { torneioId, categoriaId } = useParams<{ torneioId: string; categoriaId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const { canManageTournaments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);
  const auth = useAuth();
  const isAdmin = (auth?.user as { role?: string } | null)?.role === 'ADMIN';

  // Validação de parâmetros de rota
  if (!torneioId || !categoriaId) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        <h5 className="alert-heading">Parâmetros Inválidos</h5>
                        <p className="mb-0">
                          Os parâmetros da rota estão ausentes ou inválidos. Por favor, acesse a
                          página através do link correto.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/tournaments')}
                      >
                        Voltar aos Torneios
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  const [tabValue, setTabValue] = React.useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  });
  const [categoria, setCategoria] = useState<CategoriaTorneio | null>(null);
  const [loadingCategoria, setLoadingCategoria] = useState(false);
  const [errorCategoria, setErrorCategoria] = useState<Error | null>(null);
  const [filasEspera, setFilasEspera] = useState<FilaEspera[]>([]);
  const [loadingFilas, setLoadingFilas] = useState(false);
  const [errorFilas, setErrorFilas] = useState<Error | null>(null);
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [promotingFila, setPromotingFila] = useState(false);

  // Query para buscar dados do torneio
  const { data: torneioData, loading: loadingTorneio, error: errorTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: torneioId! },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  // Query para buscar inscrições
  const { data: inscricoesData, loading: loadingInscricoes, refetch: refetchInscricoes } = useQuery<
    InscricoesPorTorneioData,
    InscricoesPorTorneioVars
  >(INSCRICOES_POR_TORNEIO, {
    variables: { torneioId: torneioId! },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  // Buscar categoria
  const fetchCategoria = async () => {
    if (!categoriaId) return;
    setLoadingCategoria(true);
    try {
      const cat = await buscarCategoriaTorneioPorId(categoriaId);
      setCategoria(cat);
      setErrorCategoria(null);
    } catch (error) {
      setErrorCategoria(error as Error);
    } finally {
      setLoadingCategoria(false);
    }
  };

  useEffect(() => {
    fetchCategoria();
  }, [categoriaId]);

  // Sincronizar tabValue com searchParams quando a URL mudar
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const newTabValue = parseInt(tabParam, 10);
      if (!isNaN(newTabValue) && newTabValue !== tabValue) {
        setTabValue(newTabValue);
      }
    }
  }, [searchParams]);

  // Buscar filas de espera
  useEffect(() => {
    if (!categoriaId) return;

    const fetchFilas = async () => {
      setLoadingFilas(true);
      try {
        const filas = await buscarFilasEsperaPorCategoria(categoriaId);
        setFilasEspera(filas);
        setErrorFilas(null);
      } catch (error) {
        setErrorFilas(error as Error);
      } finally {
        setLoadingFilas(false);
      }
    };

    fetchFilas();
  }, [categoriaId]);

  const torneio = torneioData?.torneioPorId;
  const inscricoes = inscricoesData?.inscricoesTorneioPorAtletaIdTorneioId || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    navigate(`/liga/tournament/${torneioId}`);
  };

  const handleToggleRegistration = async () => {
    if (!categoria) return;
    
    setUpdatingStatus(true);
    try {
      const newStatus = categoria.status === StatusCategoriaTorneio.INSCRICAO_ABERTA 
        ? StatusCategoriaTorneio.INSCRICAO_ENCERRADA 
        : StatusCategoriaTorneio.INSCRICAO_ABERTA;
      
      await alterarStatusCategoriaTorneio(categoria.id, newStatus);
      
      setCategoria({ ...categoria, status: newStatus });
      setShowToggleDialog(false);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePromoverFilaEsperaPorCategoria = async (catId: string) => {
    setPromotingFila(true);
    try {
      await promoverFilaEspera({ categoriaTorneioId: catId });
      dispatchToast('Inscrição promovida da fila de espera!', 'success');
      await refetchInscricoes();
      const filas = await buscarFilasEsperaPorCategoria(catId);
      setFilasEspera(filas);
    } catch (error) {
      console.error('Erro ao promover fila de espera:', error);
      dispatchToast('Erro ao promover inscrição da fila de espera.', 'error');
    } finally {
      setPromotingFila(false);
    }
  };

  const handlePromoverFilaEsperaPorId = async (filaEsperaId: string) => {
    setPromotingFila(true);
    try {
      await promoverFilaEspera({ filaEsperaId });
      dispatchToast('Inscrição promovida da fila de espera!', 'success');
      await refetchInscricoes();
      if (categoriaId) {
        const filas = await buscarFilasEsperaPorCategoria(categoriaId);
        setFilasEspera(filas);
      }
    } catch (error) {
      console.error('Erro ao promover fila de espera:', error);
      dispatchToast('Erro ao promover inscrição da fila de espera.', 'error');
    } finally {
      setPromotingFila(false);
    }
  };

  const getToggleButtonText = () => {
    return categoria?.status === StatusCategoriaTorneio.INSCRICAO_ABERTA 
      ? 'Encerrar Inscrições' 
      : 'Abrir Inscrições';
  };

  const getToggleDialogText = () => {
    return categoria?.status === StatusCategoriaTorneio.INSCRICAO_ABERTA 
      ? 'Tem certeza que deseja encerrar as inscrições desta categoria?' 
      : 'Tem certeza que deseja abrir as inscrições desta categoria?';
  };

  const isToggleDisabled = categoria?.status === StatusCategoriaTorneio.ENCERRADA;

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  // Verificar permissão de gerenciamento de torneios
  if (!canManageTournaments) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para gerenciar torneios desta Liga. Apenas
                          administradores e gerentes podem realizar esta ação.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (loadingTorneio || loadingInscricoes || loadingCategoria || loadingFilas) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  // Tratamento de erro para filas de espera (não crítico, pode continuar sem dados)
  // O erro será exibido como um alerta discreto no componente CategoryAboutTab

  if (errorTorneio || !torneio) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Erro ao carregar torneio. Tente novamente.
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate(`/liga/tournament/${torneioId}`)}
                      >
                        Voltar ao Torneio
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (errorCategoria || !categoria) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Erro ao carregar categoria. Tente novamente.
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate(`/liga/tournament/${torneioId}`)}
                      >
                        Voltar ao Torneio
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="mb-3">
                  <PageBreadcrumb
                    items={[
                      { label: 'Dashboard', href: '/liga/dashboard' },
                      { label: 'Torneios', href: '/liga/tournaments' },
                      { label: torneio?.nome ?? 'Torneio', href: `/liga/tournament/${torneioId}` },
                      { label: categoria?.nome ?? 'Categoria' },
                    ]}
                    onBack={() => navigate(`/liga/tournament/${torneioId}`)}
                    variant="bootstrap"
                  />
                </div>
                {/* Cabeçalho do Torneio */}
                <TournamentHeader torneio={torneio} onEdit={handleEdit} />


                {/* Informações da Categoria */}
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4 className="fw-700 mb-0">{categoria.nome}</h4>
                      <CategoriaStatusChip
                        status={categoria.status}
                        temPartidas={(categoria.fasesCategoria || []).flatMap((f) => f.partidas || []).filter((p) => p.status !== 'CANCELADA').length > 0}
                      />
                    </div>
                  </div>
                </div>

                {/* Sistema de Abas */}
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                  <div className="card-body p-4">
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      aria-label="abas da categoria"
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      <Tab label="Sobre" />
                      <Tab label="Inscrições" />
                      <Tab label="Partidas" />
                      <Tab label="Configuração" />
                    </Tabs>

                    {/* Conteúdo das Abas */}
                    {tabValue === 0 && (
                      <CategoryAboutTab
                        categoriaId={categoriaId}
                        categoria={categoria}
                        inscricoes={inscricoes}
                        filasEspera={filasEspera}
                        torneioId={torneioId}
                        navigate={navigate}
                        errorFilas={errorFilas}
                        onToggleRegistration={() => setShowToggleDialog(true)}
                        isToggleDisabled={isToggleDisabled}
                        updatingStatus={updatingStatus}
                        toggleButtonText={getToggleButtonText()}
                        onRefreshCategory={fetchCategoria}
                      />
                    )}

                    {tabValue === 1 && (
                      <CategoryRegistrationsTab
                        categoriaId={categoriaId}
                        inscricoes={inscricoes}
                        torneio={torneio}
                        onPromoverFilaEsperaPorCategoria={handlePromoverFilaEsperaPorCategoria}
                        onPromoverFilaEsperaPorId={handlePromoverFilaEsperaPorId}
                        promotingFila={promotingFila}
                      />
                    )}

                    {tabValue === 2 && (
                      <CategoryMatchesTab
                        categoriaId={categoriaId}
                        torneioId={torneioId}
                        categoria={categoria}
                        inscricoes={inscricoes}
                        canManage={canManageTournaments}
                        isAdmin={isAdmin}
                        onRefresh={fetchCategoria}
                      />
                    )}

                    {tabValue === 3 && (
                      <CategoryConfigTab
                        categoriaId={categoriaId}
                        categoria={categoria}
                        torneioId={torneioId}
                        navigate={navigate}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Appfooter />
      
      {/* Dialog de Confirmação */}
      <Dialog open={showToggleDialog} onClose={() => setShowToggleDialog(false)}>
        <DialogTitle>Confirmar Ação</DialogTitle>
        <DialogContent>
          <p>{getToggleDialogText()}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowToggleDialog(false)}>Cancelar</Button>
          <Button onClick={handleToggleRegistration} variant="contained" disabled={updatingStatus}>
            {updatingStatus ? 'Atualizando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeagueCategoryManagement;

